import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { fetchAvailabilityMonth, fetchAvailabilityDay } from "@/lib/api";

const toStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function BookingCalendar({ date, time, onDate, onTime }) {
  const [month, setMonth] = useState(date ? new Date(`${date}T00:00:00`) : new Date());
  const [days, setDays] = useState({});
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    fetchAvailabilityMonth(month.getFullYear(), month.getMonth() + 1)
      .then((r) => setDays(r.days || {}))
      .catch(() => setDays({}));
  }, [month]);

  useEffect(() => {
    if (!date) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    fetchAvailabilityDay(date)
      .then((r) => setSlots(r.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const selected = date ? new Date(`${date}T00:00:00`) : undefined;
  const todayStr = toStr(new Date());
  const isDisabled = (d) => {
    const s = toStr(d);
    return s < todayStr || !days[s];
  };

  return (
    <div className="mt-7 grid gap-8 md:grid-cols-2" data-testid="booking-calendar">
      <div>
        <span className="kicker">Preferred Date</span>
        <div className="mt-3 rounded-sm border border-[#D4AF37]/30 bg-white/60 p-3">
          <Calendar
            mode="single"
            selected={selected}
            month={month}
            onMonthChange={setMonth}
            onSelect={(d) => {
              if (!d) return;
              onDate(toStr(d));
              onTime("");
            }}
            disabled={isDisabled}
            className="mx-auto"
          />
        </div>
        <p className="mt-3 text-xs font-light leading-relaxed text-[#8C7E70]">
          Only dates Natalia has opened for appointments are selectable.
        </p>
      </div>

      <div>
        <span className="kicker">Preferred Time</span>
        {!date ? (
          <p className="mt-3 text-sm font-light text-[#8C7E70]" data-testid="booking-time-hint">
            Choose a date to see available times.
          </p>
        ) : loadingSlots ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-[#8C7E70]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading times…
          </div>
        ) : slots.length === 0 ? (
          <p className="mt-3 text-sm font-light text-[#8C7E70]" data-testid="booking-no-slots">
            No times available on this date — please pick another.
          </p>
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {slots.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => onTime(t)}
                className={`rounded-full border px-3 py-3 text-sm tracking-wide transition-colors duration-200 ${
                  time === t
                    ? "border-[#1C1815] bg-[#1C1815] text-[#FAF7F2]"
                    : "border-[#D4AF37]/40 text-[#4A423A] hover:border-[#B89759]"
                }`}
                data-testid={`booking-time-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
